import * as React from 'react';

import {Avatar} from '../../../avatar/avatar';
import {RemoveButton} from '../../../button/remove-button/remove-button';
import {Dialog} from '../../../dialog/dialog';
import {ScrollableContainerWithShadow} from '../../../layout/scrollable-container-with-shadow/scrollable-container-with-shadow';
import {RelativeTimestamp} from '../../../timestamp/relative-timestamp';

import {AddReactionButton} from '../../reaction-bar/add-reaction-button';
import {ReactionBar} from '../../reaction-bar/reaction-bar';
import type {Reaction} from '../../reaction-bar/types';

import type {Comment} from './types';

import styles from './timeline-comment.module.css';

type Props = {
    comment: Comment;
    onDeleteComment?: () => any;
    onToggleReaction?: (reaction: Reaction) => any;
    currentUserId?: string;
    limitCommentHeight?: boolean;
};

export function TimelineComment(props: Props) {
    const {onDeleteComment, onToggleReaction, currentUserId, limitCommentHeight} = props;
    const {creatorUser, bodyHtml, createdTime, reactions} = props.comment;

    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false);
    const deleteButtonRef = React.useRef(null);

    const displayableBodyHtml = (
        <div
            dangerouslySetInnerHTML={{
                // @ts-expect-error
                __html: bodyHtml,
            }}
        />
    );

    return (
        <div className={styles['timeline-comment']}>
            <div className={styles['avatar-container']}>
                <Avatar
                    initials={creatorUser.initials}
                    avatarUrl={creatorUser.avatarUrl}
                    type='users'
                    circular={true}
                    size={24}
                />
            </div>
            <div className={styles['commenter-content-container']}>
                <div>{creatorUser.name}</div>
                <div className={styles.content}>
                    {limitCommentHeight ? (
                        <ScrollableContainerWithShadow
                            direction='vertical'
                            maxContainerSize={150}
                            showReverseGradientShadow={true}
                        >
                            {displayableBodyHtml}
                        </ScrollableContainerWithShadow>
                    ) : (
                        displayableBodyHtml
                    )}
                </div>
                {reactions && Object.keys(reactions).length && onToggleReaction && currentUserId ? (
                    <div className={styles['reaction-bar-container']}>
                        <ReactionBar
                            hideAddButton={true}
                            currentUserId={currentUserId}
                            reactions={reactions}
                            onToggleReaction={onToggleReaction}
                            isSmall={true}
                        />
                    </div>
                ) : undefined}
            </div>
            <div className={styles['timestamp-delete-button-container']}>
                <RelativeTimestamp datetime={createdTime} />
                {onToggleReaction ? (
                    <div className={styles['add-reaction-container']}>
                        <AddReactionButton onSelectReaction={onToggleReaction} />
                    </div>
                ) : undefined}
                {onDeleteComment ? (
                    <div className={styles['remove-button']}>
                        <RemoveButton
                            size={12}
                            tooltip='Delete'
                            onClick={() => setIsConfirmingDelete(true)}
                            getButtonRef={deleteButtonRef}
                        />
                    </div>
                ) : undefined}
            </div>
            {isConfirmingDelete && onDeleteComment ? (
                <Dialog
                    anchor={deleteButtonRef.current}
                    question='Are you sure you want to delete this comment?'
                    primaryText='Yes, delete comment'
                    secondaryText='No, don’t delete'
                    onPrimary={() => {
                        onDeleteComment().then(setIsConfirmingDelete(false));
                    }}
                    onSecondary={() => setIsConfirmingDelete(false)}
                    isWarning={true}
                    customStyles={{width: '290px'}}
                />
            ) : undefined}
        </div>
    );
}
